import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, EmptyState, Skeleton, ToggleSwitch, useLocale, useToast } from '@ministry/ui/internal';
import BellOff from 'lucide-react/dist/esm/icons/bell-off.js';

import { listNotifications, markAllNotificationsRead, markNotificationRead, type NotificationRow } from '../lib/notifications.js';
import { ROUTES } from '../lib/nav.js';
import { useT } from '../i18n/strings.js';
import { formatInstantDisplay } from '../lib/dates.js';
import { PageHeader } from '../components/PageHeader.js';
import { ServerErrorState } from '../components/ServerErrorState.js';

const PAGE_LIMIT = 20;

/**
 * SCREEN_SPECS.md I11's full-page form — the bell/panel (`AppShell`'s `NotificationBell`)
 * is the always-available quick view; this is the "see everything, filter, page back
 * through history" screen the spec also names, reached from the nav (gated on
 * `notification.read`, the same permission the bell itself checks).
 */
export function NotificationCentrePage(): React.JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const { push } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [unreadOnly, setUnreadOnly] = React.useState(false);

  const query = useInfiniteQuery({
    queryKey: ['notifications', 'centre', unreadOnly],
    queryFn: ({ pageParam, signal }) => listNotifications({ unread: unreadOnly || undefined, cursor: pageParam, limit: PAGE_LIMIT }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.page.hasMore ? (lastPage.page.nextCursor ?? undefined) : undefined),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const markReadMutation = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: invalidate,
  });

  const markAllMutation = useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => {
      invalidate();
      push({ variant: 'success', title: t('toast.notifications.markedAll') });
    },
  });

  const rows: NotificationRow[] = query.data?.pages.flatMap((page) => page.items) ?? [];
  const hasUnread = rows.some((row) => row.readAt === null);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('notifications.centre.title')}
        description={t('notifications.centre.description')}
        breadcrumb={[{ label: t('shell.dashboard'), href: ROUTES.dashboard }, { label: t('notifications.centre.title') }]}
        actions={
          hasUnread ? (
            <Button variant="secondary" size="sm" onClick={() => markAllMutation.mutate()} loading={markAllMutation.isPending}>
              {t('notifications.markAllRead')}
            </Button>
          ) : undefined
        }
      />

      <ToggleSwitch id="notifications-unread-only" label={t('notifications.centre.unreadOnly')} checked={unreadOnly} onCheckedChange={setUnreadOnly} />

      {query.isLoading ? (
        <Skeleton shape="list-row" count={5} />
      ) : query.isError ? (
        <ServerErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<BellOff aria-hidden="true" width={32} height={32} />}
          title={unreadOnly ? t('notifications.centre.empty.unread.title') : t('notifications.centre.empty.title')}
          body={unreadOnly ? t('notifications.centre.empty.unread.body') : t('notifications.centre.empty.body')}
        />
      ) : (
        <ul className="flex flex-col divide-y divide-border-decorative rounded-md border border-border-decorative">
          {rows.map((row) => {
            const unread = row.readAt === null;
            return (
              <li key={row.id}>
                <button
                  type="button"
                  className={`flex w-full flex-col gap-1 px-4 py-3 text-left hover:bg-surface-hover focus-visible:outline-none focus-visible:shadow-focus ${unread ? 'bg-surface-selected' : ''}`}
                  onClick={() => {
                    if (unread) markReadMutation.mutate(row.id);
                    if (row.subjectType === 'file' && row.subjectId) navigate(ROUTES.fileDetail(row.subjectId));
                    else if (row.subjectType === 'appointment' && row.subjectId) navigate(ROUTES.appointmentDetail(row.subjectId));
                  }}
                >
                  <span className="flex items-center gap-2">
                    {unread ? <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-status-info-solid" /> : null}
                    <span className={unread ? 'font-semibold text-text-heading' : 'text-text-primary'}>{row.body}</span>
                  </span>
                  <span className="text-xs text-text-muted">{formatInstantDisplay(row.createdAt, locale)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {query.hasNextPage ? (
        <Button variant="secondary" onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
          {t('notifications.centre.loadMore')}
        </Button>
      ) : null}
    </div>
  );
}
